/* eslint-disable react/no-array-index-key, react/no-danger */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';

import { getImagem } from '../utils/imagens';
import Footer from '../components/shared/Footer';
import PremiacoesPorRegiao from '../components/shared/PremiacoesPorRegiao';
import {
  itemPremiacaoPropType,
  dadosPremiacaoPropType,
  cidadePremiacaoPropType
} from '../types/premiacoes';

import '../assets/scss/components/PremiacoesPage.scss';

const CardVencedor = ({ item, clear }) => {
  const iconeSrc = item.icone ? getImagem(item.icone) : null;
  const fotoSrc = item.foto ? getImagem(item.foto) : null;

  return (
    <div className={`premiacoes-card${clear ? ' premiacoes-card--clear' : ''}`}>
      <div className="premiacoes-card__header">
        {iconeSrc && <img src={iconeSrc} alt={item.categoria} />}
        <h3 dangerouslySetInnerHTML={{ __html: item.categoria }} />
      </div>
      {fotoSrc && (
        <img
          src={fotoSrc}
          className="premiacoes-card__image"
          alt={item.vencedor}
        />
      )}
      <h4
        className="premiacoes-card__winner"
        dangerouslySetInnerHTML={{ __html: item.vencedor }}
      />
    </div>
  );
};

CardVencedor.propTypes = {
  item: itemPremiacaoPropType.isRequired,
  clear: PropTypes.bool
};

CardVencedor.defaultProps = {
  clear: false
};

const PremiacoesPage = ({ cidade, premiacao }) => {
  const history = useHistory();
  const selectRef = useRef(null);
  const logoSrc = cidade.footer.logoSmall
    ? getImagem(cidade.footer.logoSmall)
    : null;

  const anos =
    premiacao.anosDisponiveis && premiacao.anosDisponiveis.length > 0
      ? premiacao.anosDisponiveis
      : [String(premiacao.ano || 2026)];
  const [anoAtivo, setAnoAtivo] = useState(anos[0]);
  const [dropdownAberto, setDropdownAberto] = useState(false);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (selectRef.current && !selectRef.current.contains(e.target)) {
        setDropdownAberto(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  const dadosAno = premiacao.historico?.[anoAtivo] || {
    horsConcours: premiacao.horsConcours || [],
    premiacao: premiacao.premiacao || []
  };

  const hasHorsConcours =
    dadosAno.horsConcours && dadosAno.horsConcours.length > 0;
  const hasPremiacao = dadosAno.premiacao && dadosAno.premiacao.length > 0;
  const hasRegioes = dadosAno.regioes && dadosAno.regioes.length > 0;

  return (
    <div className="premiacoes-page">
      {/* Header */}
      <div className="premiacoes-page__header-wrapper">
        <button
          type="button"
          className="premiacoes-page__back-btn"
          onClick={() => history.push(cidade.pagina)}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M19 12H5M5 12L12 19M5 12L12 5"
              stroke="#7B3603"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>VOLTAR</span>
        </button>
        {logoSrc && (
          <img
            className="premiacoes-page__logo"
            src={logoSrc}
            alt={cidade.footer.logoAlt || cidade.nome}
          />
        )}
      </div>

      {/* Conteúdo Principal */}
      <div className="premiacoes-page__container">
        <div className="premiacoes-page__intro">
          <h1>A PREMIAÇÃO</h1>
          <p>
            Por meio do voto popular e de um júri composto por especialistas em
            gastronomia e conhecedores da cidade, vamos eleger as melhores
            padarias de {cidade.nome},{' '}
            {hasRegioes ? 'em cada região' : 'em diversas categorias'}.
          </p>
          {anos.length > 1 && (
            <div className="premiacoes-page__year-selector">
              <img
                className="trophy-icon"
                src={getImagem('categorias/trofeu-padocaria.webp')}
                alt="Troféu"
              />
              <div className="premiacoes-page__year-selector-content">
                <span>Selecione o ano:</span>
                <div className="premiacoes-custom-select" ref={selectRef}>
                  <button
                    type="button"
                    className="premiacoes-custom-select__trigger"
                    onClick={() => setDropdownAberto(!dropdownAberto)}
                  >
                    <span>{anoAtivo}</span>
                  </button>
                  {dropdownAberto && (
                    <ul className="premiacoes-custom-select__options">
                      {anos.map((ano) => (
                        <li key={ano}>
                          <button
                            type="button"
                            className={ano === anoAtivo ? 'active' : ''}
                            onClick={() => {
                              setAnoAtivo(ano);
                              setDropdownAberto(false);
                            }}
                          >
                            {ano}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {hasHorsConcours || hasPremiacao || hasRegioes ? (
          <>
            {hasHorsConcours && (
              <div className="premiacoes-page__section">
                <h2>PREMIAÇÃO - HORS CONCOURS</h2>
                <div className="premiacoes-page__grid">
                  {dadosAno.horsConcours.map((item, idx) => (
                    <CardVencedor key={idx} item={item} />
                  ))}
                </div>
              </div>
            )}

            {hasRegioes && <PremiacoesPorRegiao regioes={dadosAno.regioes} />}

            {hasPremiacao && (
              <div className="premiacoes-page__section">
                <h2>PREMIAÇÃO</h2>
                <div className="premiacoes-page__grid">
                  {dadosAno.premiacao.map((item, idx) => (
                    <CardVencedor key={idx} item={item} clear />
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="premiacoes-page__empty">
            <p>
              Os resultados da premiação de {cidade.nome} serão divulgados em
              breve.
            </p>
          </div>
        )}
      </div>

      <Footer city={cidade} cidade={cidade} />
    </div>
  );
};

PremiacoesPage.propTypes = {
  cidade: cidadePremiacaoPropType.isRequired,
  premiacao: dadosPremiacaoPropType.isRequired
};

export default PremiacoesPage;
